import { useEffect } from 'react'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'

export interface UseDrawingModeProps {
  isDrawMode: boolean
  onDrawingPointAdd?: (coords: [number, number]) => void
  onDrawingHover?: (coords: [number, number] | null) => void
}

/**
 * Hook for managing drawing mode event handlers
 * Handles click and hover events when in drawing mode
 */
export function useDrawingMode({
  isDrawMode,
  onDrawingPointAdd,
  onDrawingHover
}: UseDrawingModeProps) {
  const { map, isLoaded } = useMapboxMapContext()

  useEffect(() => {
    if (!map.current || !isLoaded) return

    const handleDrawingClick = (e: mapboxgl.MapMouseEvent) => {
      // Don't add drawing points if clicking on a route or waypoint
      if (e.features && e.features.length > 0) {
        return
      }
      if (onDrawingPointAdd) {
        const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        onDrawingPointAdd(coords)
      }
    }

    const handleDrawingHover = (e: mapboxgl.MapMouseEvent) => {
      if (onDrawingHover) {
        const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        onDrawingHover(coords)
      }
    }

    if (isDrawMode && onDrawingPointAdd && onDrawingHover) {
      const drawingClickWrapper = (e: mapboxgl.MapMouseEvent) => {
        // Don't add drawing points if clicking on a route or waypoint
        if (e.features && e.features.length > 0) {
          return
        }
        // Only handle drawing clicks - don't prevent other handlers from running
        handleDrawingClick(e)
      }

      map.current.on('click', drawingClickWrapper)
      map.current.on('mousemove', handleDrawingHover)
      map.current.on('mouseleave', () => {
        if (onDrawingHover) onDrawingHover(null)
      })

      return () => {
        if (map.current) {
          map.current.off('click', drawingClickWrapper)
          map.current.off('mousemove', handleDrawingHover)
        }
      }
    }
  }, [map, isLoaded, isDrawMode, onDrawingPointAdd, onDrawingHover])
}

